import { Injectable } from '@angular/core';

type PrintOptions = {
  title?: string;
  a4MarginMm?: { top: number; right: number; bottom: number; left: number };
};

@Injectable({ providedIn: 'root' })
export class PrintPdfService {
  async printHtml(html: string, options?: PrintOptions): Promise<void> {
    const margin = options?.a4MarginMm ?? { top: 13, right: 18, bottom: 13, left: 18 };
    const printCss = `
      @page { size: A4; margin: ${margin.top}mm ${margin.right}mm ${margin.bottom}mm ${margin.left}mm; }
      html, body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* Avoid weird default margins in print preview */
      body { margin: 0; }
      /* Help pagination for large sections */
      section { break-inside: avoid; page-break-inside: avoid; }
    `;

    const withPrintCss = this.injectCssIntoHtml(html, printCss);

    // Use an offscreen iframe to avoid opening a new tab/window.
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) throw new Error('Unable to initialize print frame');

      doc.open();
      doc.write(withPrintCss);
      doc.close();

      await this.waitForReady(doc, win);
      if (options?.title) doc.title = options.title;

      await new Promise<void>((resolve) => {
        const done = () => {
          win.removeEventListener('afterprint', done);
          resolve();
        };
        win.addEventListener('afterprint', done, { once: true });
        // Some browsers ignore focusing an iframe; still safe.
        win.focus();
        win.print();
      });
    } finally {
      document.body.removeChild(iframe);
    }
  }

  private injectCssIntoHtml(html: string, css: string): string {
    const styleTag = `<style id="__print_css__">${css}</style>`;
    if (html.includes('</head>')) {
      return html.replace('</head>', `${styleTag}</head>`);
    }
    return `${styleTag}${html}`;
  }

  private async waitForReady(doc: Document, win: Window): Promise<void> {
    await new Promise<void>((resolve) => {
      if (doc.readyState === 'complete') return resolve();
      win.addEventListener('load', () => resolve(), { once: true });
    });
    await (doc as any).fonts?.ready?.catch?.(() => undefined);
    // Give layout a beat to settle (fonts can swap after ready in some cases).
    await new Promise((r) => setTimeout(r, 50));
  }
}

